import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  StatusBar,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchLinkedAccounts, deleteLinkedAccount } from '@/store/slices/residents/homeSlice';
import { fetchInvitations, declineInvitation, sendInvitation } from '@/store/slices/residents/invitationsSlice';
import { LinkedAccount, Invitation } from '@/types/residents/home';
import { useToast } from '@/contexts/admin/ToastContext';
import { LINKED_ACCOUNTS_COLORS } from '@/constants/residents/mobile/linkedAccountsColors';
import { getDisplayName } from '@/utils/residents/utils';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';

// Layout components
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { GradientBorderFrame } from '@/components/common/admin/mobile/GradientBorderFrame';

// Linked accounts components
import { LinkedAccountsNavHeader, TabType } from '@/components/residents/linkedAccounts/mobile/LinkedAccountsNavHeader';
import { MobileLinkedAccountsList } from '@/components/residents/linkedAccounts/mobile/MobileLinkedAccountsList';
import { MobileInvitationsList } from '@/components/residents/linkedAccounts/mobile/MobileInvitationsList';
import { NewInvitationModal } from '@/components/residents/linkedAccounts/mobile/NewInvitationModal';
import { EmailPopupModal } from '@/components/residents/linkedAccounts/mobile/EmailPopupModal';

// Reusable marketplace modal
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';

/**
 * Resident Mobile Linked Accounts Screen
 * Uses new Figma design with gradient frame, dark theme, tabs, and modal integration
 */
export const ResidentLinkedAccountsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { linkedAccounts, isLoading } = useAppSelector((state) => state.home);
  const { showSuccess, showError } = useToast();

  // Use resident screen hook for navigation handling
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('ResidentLinkedAccounts', navigation);

  // Tab state
  const [selectedTab, setSelectedTab] = useState<TabType>('linkedAccounts');

  // Remove Connection Modal states
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState<LinkedAccount | null>(null);

  // Email Popup Modal states
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');

  // New Invitation Modal states
  const [isNewInvitationModalVisible, setIsNewInvitationModalVisible] = useState(false);

  // Decline Invitation Modal states
  const [isDeclineModalVisible, setIsDeclineModalVisible] = useState(false);
  const [invitationToDecline, setInvitationToDecline] = useState<Invitation | null>(null);

  // Cancel Invitation Modal states
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<Invitation | null>(null);

  // Load linked accounts on mount
  useEffect(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  // Handle tab change
  const handleTabChange = useCallback((tab: TabType) => {
    setSelectedTab(tab);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  // ============================================================================
  // LINKED ACCOUNTS - Remove Connection
  // ============================================================================

  const handleRemovePress = useCallback((account: LinkedAccount) => {
    setAccountToRemove(account);
    setIsRemoveModalVisible(true);
  }, []);

  const handleRemoveCancel = useCallback(() => {
    setIsRemoveModalVisible(false);
    setAccountToRemove(null);
  }, []);

  const handleRemoveProceed = useCallback(async () => {
    if (!accountToRemove) return;

    setIsRemoveModalVisible(false);
    
    try {
      await dispatch(deleteLinkedAccount(accountToRemove.id)).unwrap();
      const displayName = getDisplayName(
        accountToRemove.user.firstName,
        accountToRemove.user.lastName
      ) || accountToRemove.user.email;
      showSuccess(`You have successfully disconnected from ${displayName}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove linked account';
      showError(message);
    } finally {
      setAccountToRemove(null);
    }
  }, [accountToRemove, dispatch, showSuccess, showError]);

  const getRemoveConfirmMessage = (): string => {
    if (!accountToRemove) return '';
    const displayName = getDisplayName(
      accountToRemove.user.firstName,
      accountToRemove.user.lastName
    ) || accountToRemove.user.email;
    return `Are you sure you want to remove connection to ${displayName}?`;
  };

  // ============================================================================
  // EMAIL POPUP
  // ============================================================================

  const handleEmailPress = useCallback((email: string) => {
    setSelectedEmail(email);
    setIsEmailModalVisible(true);
  }, []);

  const handleEmailModalClose = useCallback(() => {
    setIsEmailModalVisible(false);
    setSelectedEmail('');
  }, []);

  // ============================================================================
  // NEW INVITATION MODAL
  // ============================================================================

  const handleSendInvite = useCallback(() => {
    setIsNewInvitationModalVisible(true);
  }, []);

  const handleNewInvitationClose = useCallback(() => {
    setIsNewInvitationModalVisible(false);
  }, []);

  const handleSendInvitation = useCallback(async (email: string, message: string) => {
    try {
      await dispatch(sendInvitation({ email, message })).unwrap();
      setIsNewInvitationModalVisible(false);
      showSuccess(`Invitation sent to ${email}`);
      dispatch(fetchInvitations());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation';
      showError(errorMessage);
      throw error;
    }
  }, [dispatch, showSuccess, showError]);


  // ============================================================================
  // DECLINE INVITATION
  // ============================================================================

  const handleDeclineInvitation = useCallback((invitation: Invitation) => {
    setInvitationToDecline(invitation);
    setIsDeclineModalVisible(true);
  }, []);

  const handleDeclineCancel = useCallback(() => {
    setIsDeclineModalVisible(false);
    setInvitationToDecline(null);
  }, []);

  const handleDeclineProceed = useCallback(async () => {
    if (!invitationToDecline) return;

    setIsDeclineModalVisible(false);
    
    try {
      await dispatch(declineInvitation({ 
        invitationId: invitationToDecline.id, 
        sender: invitationToDecline.sender 
      })).unwrap();
      const displayName = getDisplayName(
        invitationToDecline.user.firstName,
        invitationToDecline.user.lastName
      ) || invitationToDecline.user.email;
      showSuccess(`You have successfully declined an invitation from ${displayName}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to decline invitation';
      showError(message);
    } finally {
      setInvitationToDecline(null);
    }
  }, [invitationToDecline, dispatch, showSuccess, showError]);

  const getDeclineConfirmMessage = (): string => {
    if (!invitationToDecline) return '';
    const displayName = getDisplayName(
      invitationToDecline.user.firstName,
      invitationToDecline.user.lastName
    ) || invitationToDecline.user.email;
    return `Are you sure you want to decline an invitation from ${displayName}?`;
  };

  // ============================================================================
  // CANCEL INVITATION
  // ============================================================================

  const handleCancelInvitation = useCallback((invitation: Invitation) => {
    setInvitationToCancel(invitation);
    setIsCancelModalVisible(true);
  }, []);

  const handleCancelConfirmCancel = useCallback(() => {
    setIsCancelModalVisible(false);
    setInvitationToCancel(null);
  }, []);

  const handleCancelProceed = useCallback(async () => {
    if (!invitationToCancel) return;

    setIsCancelModalVisible(false);
    
    try {
      await dispatch(declineInvitation({ 
        invitationId: invitationToCancel.id, 
        sender: invitationToCancel.sender 
      })).unwrap();
      showSuccess(`Invitation cancelled for ${invitationToCancel.user.email}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cancel invitation';
      showError(message);
    } finally {
      setInvitationToCancel(null);
    }
  }, [invitationToCancel, dispatch, showSuccess, showError]);

  const getCancelConfirmMessage = (): string => {
    if (!invitationToCancel) return '';
    return `Are you sure you want to cancel an invitation for ${invitationToCancel.user.email}?`;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const renderContent = () => {
    if (selectedTab === 'linkedAccounts') {
      return (
        <MobileLinkedAccountsList
          accounts={linkedAccounts}
          isLoading={isLoading}
          onRefresh={handleRefresh}
          onRemove={handleRemovePress}
          onEmailPress={handleEmailPress}
        />
      );
    }

    return (
      <MobileInvitationsList
        onSendInvite={handleSendInvite}
        onDeclineInvitation={handleDeclineInvitation}
        onCancelInvitation={handleCancelInvitation}
      />
    );
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />
      
      {/* New Figma Header with Navigation Menu */}
      <ResidentMobileHeaderWithMenu
        onLogoPress={handleLogoPress}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={handleNotificationPress}
        currentPath="/linked-accounts"
        currentTitle="Linked Accounts"
        showNotification={true}
      />
      
      {/* Outer gradient frame - 3px border, 32px radius */}
      <GradientBorderFrame
        borderWidth={3}
        borderRadius={32}
        innerStyle={styles.outerFrameInner}
      >
        <View style={styles.innerFrameSpacing}>
          {/* Inner gradient frame - 1px border, 27px radius */}
          <GradientBorderFrame
            borderWidth={1}
            borderRadius={27}
            innerStyle={styles.innerFrameContent}
          >
            {/* Nav Header with Tabs */}
            <LinkedAccountsNavHeader
              selectedTab={selectedTab}
              onTabChange={handleTabChange}
            />

            {/* Tab Content */}
            {renderContent()}
          </GradientBorderFrame>
        </View>
      </GradientBorderFrame>

      {/* MODALS */}

      {/* Remove Connection Confirmation Modal */}
      <ConfirmationModal
        visible={isRemoveModalVisible}
        message={getRemoveConfirmMessage()}
        onCancel={handleRemoveCancel}
        onProceed={handleRemoveProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />

      {/* Email Popup Modal */}
      <EmailPopupModal
        visible={isEmailModalVisible}
        email={selectedEmail}
        onClose={handleEmailModalClose}
      />

      {/* New Invitation Modal */}
      <NewInvitationModal
        visible={isNewInvitationModalVisible}
        onClose={handleNewInvitationClose}
        onSend={handleSendInvitation}
      />

      {/* Decline Invitation Confirmation Modal */}
      <ConfirmationModal
        visible={isDeclineModalVisible}
        message={getDeclineConfirmMessage()}
        onCancel={handleDeclineCancel}
        onProceed={handleDeclineProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />

      {/* Cancel Invitation Confirmation Modal */}
      <ConfirmationModal
        visible={isCancelModalVisible}
        message={getCancelConfirmMessage()}
        onCancel={handleCancelConfirmCancel}
        onProceed={handleCancelProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
  },
  outerFrameInner: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.background,
  },
  innerFrameSpacing: {
    flex: 1,
    paddingTop: 2,
    paddingHorizontal: 2,
  },
  innerFrameContent: {
    flex: 1,
    backgroundColor: LINKED_ACCOUNTS_COLORS.background,
  },
});
